import { z } from "zod";
/**
 * Workflow status — drafts are being edited, active workflows can be started
 * by their configured trigger, and paused workflows are suspended.
 */
export const WORKFLOW_STATUSES = ["draft", "active", "paused"];
export const WORKFLOW_NODE_TYPES = [
    "trigger",
    "agent",
    "condition",
    "action",
    // Platform-executed typed decision (classification / gate) — see
    // decisionDataSchema below and docs/PROPOSAL-decision-node-jev.md.
    "decision",
];
export const TRIGGER_SUBTYPES = [
    "manual",
    "schedule",
    "webhook",
    "channel_message",
    "email_received",
];
/** HTTP verbs that may intentionally start an inbound-webhook workflow. */
export const WEBHOOK_METHODS = ["POST", "GET", "PUT", "PATCH", "DELETE"];
/** Outbound methods supported by the platform-owned HTTP Request action. */
export const HTTP_REQUEST_METHODS = ["POST", "GET", "PUT", "PATCH", "DELETE"];
/**
 * How an HTTP action presents its credential. The canvas stores only which
 * secret to use; the value is read from Key Vault server-side at run time.
 */
export const HTTP_AUTH_TYPES = ["bearer", "header", "query"];
/** Response handling modes supported by the platform-owned HTTP action. */
export const HTTP_RESPONSE_TYPES = [
    "auto",
    "json",
    "text",
    "csv",
    "html",
    "pdf",
    "image",
    "empty",
];
export const ACTION_TYPES = [
    "http_request",
    "send_email",
    "wait_for_email_reply",
    "post_to_channel",
    // Generic: invoke any MCP tool the executing agent already has access to.
    // Config: { tool: string, args: Record<string, unknown>, awaiting?: { type, correlationFromOutput? } }
    // This is the runtime's primary action shape — the other types are
    // legacy/specialised forms kept for the existing canvas UI.
    "mcp_tool",
];
/**
 * Per-node-type data shapes. Kept deliberately loose (passthrough) so we can
 * evolve fields without migrations — the whole node array is a single jsonb
 * column. The server does shape-level validation on save but permits
 * unknown keys so the client can add fields freely.
 */
const triggerDataSchema = z
    .object({
    label: z.string().max(200).optional(),
    subtype: z.enum(TRIGGER_SUBTYPES).default("manual"),
    cronExpression: z.string().max(100).optional(),
    timezone: z.string().max(100).optional(),
    channelId: z.string().uuid().optional(),
    webhookId: z.string().max(200).optional(),
    method: z.enum(WEBHOOK_METHODS).optional(),
    agentId: z.string().uuid().optional(),
    from: z.string().email().max(320).optional(),
    subjectContains: z.string().max(500).optional(),
    includeAttachments: z.boolean().optional(),
    description: z.string().max(2000).optional(),
})
    .passthrough();
const agentDataSchema = z
    .object({
    label: z.string().max(200).optional(),
    agentId: z.string().uuid().optional(),
    promptTemplate: z.string().max(10000).optional(),
    description: z.string().max(2000).optional(),
    // Per-step model override — a model_catalogue modelId (e.g.
    // "claude-opus-4-8"). Absent/empty = the agent's own configured model.
    model: z.string().max(200).optional(),
    // Backup model used when the primary fails at wake time or the step is
    // being retried. The agent's own model is always the final fallback.
    fallbackModel: z.string().max(200).optional(),
    // What happens if this node parks on a wait (e.g. a Teams card) and the
    // deadline passes with no response. "fail" (default) fails the run;
    // "continue" resumes the workflow with timed_out=true so a downstream
    // Condition can branch on the timeout. Inert on nodes that never wait.
    onAwaitTimeout: z.enum(["fail", "continue"]).optional(),
    // Procedures for this step — tenant_skills ids, scope "workflow" only.
    //
    // ORDERED, and the order is load-bearing: element 0 is the PRIMARY
    // procedure the agent follows, the rest are supporting reference. The
    // bodies are resolved server-side in workflow_step_start and handed to
    // the agent for that step alone; they are never synced to a VM and never
    // enter the agent's chat system prompt, which is the whole reason this
    // field exists rather than assigning the skill to the agent.
    //
    // Capped at 5 because the combined bodies are also size-capped at
    // resolution time — see WORKFLOW_STEP_PROCEDURE_CHAR_CAP.
    skillIds: z.array(z.string().uuid()).max(5).optional(),
})
    .passthrough();
export const workflowConditionChoiceSchema = z.object({
    id: z.string().min(1).max(100),
    label: z.string().min(1).max(200),
    expression: z.string().max(2000).optional(),
    isDefault: z.boolean().optional(),
});
export const workflowConditionDataSchema = z
    .object({
    label: z.string().max(200).optional(),
    expression: z.string().max(2000).optional(),
    description: z.string().max(2000).optional(),
    mode: z.enum(["binary", "multiple"]).optional(),
    choices: z.array(workflowConditionChoiceSchema).min(2).max(8).optional(),
})
    .passthrough();
const actionDataSchema = z
    .object({
    label: z.string().max(200).optional(),
    // GET/POST are accepted only to read workflows saved before HTTP Request
    // became one method-driven action. New authoring exposes http_request only.
    actionType: z
        .union([z.enum(ACTION_TYPES), z.enum(["http_get", "http_post"])])
        .default("http_request"),
    description: z.string().max(2000).optional(),
    // Flat bag of per-action-type config — validated per action type by the
    // UI form, kept loose here.
    config: z.record(z.string(), z.unknown()).optional(),
})
    .passthrough();
// --------------------------------------------------------------------
// Decision node — a platform-executed typed decision.
//
// The node sends a small templated `state` to a decision provider (TypeSafe
// Jev today), asks one or more typed questions, and branches on the answer
// to the `route` question. It never wakes an agent VM. Every probability the
// provider returns is kept in the step outputs, so workflow_run_steps doubles
// as the calibration dataset.
// --------------------------------------------------------------------
export const DECISION_PROVIDERS = ["typesafe"];
export const DECISION_QUESTION_TYPES = ["choice", "noul", "score"];
/** Provider ceiling on options per choice question (TypeSafe: 255). */
export const DECISION_MAX_OPTIONS = 255;
/** Provider ceiling on levels per score question (TypeSafe: 2 to 10). */
export const DECISION_MAX_SCORE_LEVELS = 10;
/** Per-state-field character cap applied before the request; longer values are truncated and flagged. */
export const DECISION_STATE_MAX_CHARS_DEFAULT = 24_000;
export const DECISION_STATE_MAX_CHARS_CEILING = 60_000;
/** Wire key for the "nothing listed fits" option a choice question can carry. */
export const DECISION_NONE_KEY = "__none__";
/**
 * Outgoing handles every decision node has in addition to its value handles.
 *   none            — the routed choice picked "nothing fits" above its floor
 *   low_confidence  — the routed answer fell below the configured threshold
 *   unavailable     — the provider could not be reached after retries
 * Each is optional on the canvas; an unconnected one falls back to
 * `route.fallbackBranch`, which must be one of the value handles.
 */
export const DECISION_EXTRA_BRANCHES = ["none", "low_confidence", "unavailable"];
const decisionHandleId = z
    .string()
    .min(1)
    .max(60)
    .regex(/^[A-Za-z0-9_-]+$/, "Use letters, numbers, hyphens and underscores only");
/** One selectable option of a choice question. `value` is what downstream steps and edges see. */
export const workflowDecisionOptionSchema = z.object({
    value: decisionHandleId,
    // What the option MEANS. Free text, or a structured object (e.g. {what, not_for, examples})
    // which the provider shows to the model field by field.
    description: z.union([z.string().max(4000), z.record(z.string(), z.unknown())]).optional(),
});
export const workflowDecisionQuestionSchema = z
    .object({
    id: decisionHandleId,
    type: z.enum(DECISION_QUESTION_TYPES),
    // The question, written as a direct test. For choice: how to decide, ideally an ordered test.
    instructions: z.string().min(1).max(6000),
    // choice: static options, or a context path (steps.<id>.outputs.<key> / inputs.<key>)
    // resolving to [{ value, description }] at run time.
    options: z.array(workflowDecisionOptionSchema).max(DECISION_MAX_OPTIONS).optional(),
    optionsFrom: z.string().max(200).optional(),
    // choice: offer "nothing listed fits", honoured only when its probability is >= noneFloor.
    includeNone: z.boolean().optional(),
    noneDescription: z.string().max(1000).optional(),
    noneFloor: z.number().min(0).max(1).optional(),
    // noul: optional boundary descriptions for true / false.
    trueDescription: z.string().max(1000).optional(),
    falseDescription: z.string().max(1000).optional(),
    // score: ordered level descriptions, level 0 first.
    levels: z.array(z.string().min(1).max(1000)).min(2).max(DECISION_MAX_SCORE_LEVELS).optional(),
})
    .passthrough();
export const workflowDecisionRouteSchema = z
    .object({
    // Which question drives the outgoing edges. Defaults to the first question.
    question: decisionHandleId.optional(),
    // choice / score: below this confidence the step takes `low_confidence`.
    minConfidence: z.number().min(0).max(1).optional(),
    // noul: p >= yesThreshold → "yes"; p <= noThreshold → "no"; between → low_confidence.
    yesThreshold: z.number().min(0).max(1).optional(),
    noThreshold: z.number().min(0).max(1).optional(),
    // Value handle taken when none / low_confidence / unavailable has no edge of its own.
    fallbackBranch: decisionHandleId.optional(),
})
    .passthrough();
export const workflowDecisionDataSchema = z
    .object({
    label: z.string().max(200).optional(),
    description: z.string().max(2000).optional(),
    provider: z.enum(DECISION_PROVIDERS).default("typesafe"),
    // Provider model alias. Empty = provider default (TypeSafe: jev-latest).
    model: z.string().max(100).optional(),
    // Named state fields. Values are templates: {{input.x}} / {{steps.<id>.outputs.<key>}}.
    // Send ONLY what the decision needs — accuracy falls as irrelevant state grows.
    state: z.record(z.string().min(1).max(60), z.string().max(20_000)).optional(),
    stateMaxChars: z
        .number()
        .int()
        .min(200)
        .max(DECISION_STATE_MAX_CHARS_CEILING)
        .optional(),
    questions: z.array(workflowDecisionQuestionSchema).min(1).max(20).optional(),
    route: workflowDecisionRouteSchema.optional(),
    timeoutMs: z.number().int().min(1000).max(60_000).optional(),
})
    .passthrough();
/**
 * The outgoing value handles a decision node exposes, derived from its routed
 * question. Shared by the canvas (which draws one handle per entry), the graph
 * validator (which checks each has an edge) and the runner (which picks the
 * edge). Returns [] when the routed question cannot be resolved statically —
 * a choice bound via `optionsFrom` has no fixed handle set, which is why a
 * routed question must use static options.
 */
export function decisionValueHandles(data) {
    const d = (data ?? {});
    const questions = Array.isArray(d.questions) ? d.questions : [];
    const routedId = d.route?.question;
    const q = (routedId ? questions.find((x) => x?.id === routedId) : questions[0]);
    if (!q)
        return [];
    if (q.type === "noul")
        return ["yes", "no"];
    if (q.type === "score") {
        const n = Array.isArray(q.levels) ? q.levels.length : 0;
        return Array.from({ length: n }, (_, i) => String(i));
    }
    if (q.type === "choice" && Array.isArray(q.options)) {
        return q.options
            .map((o) => (o && typeof o.value === "string" ? o.value : ""))
            .filter((v) => v.length > 0);
    }
    return [];
}
/** The question a decision node routes on (explicit `route.question`, else the first). */
export function decisionRoutedQuestion(data) {
    const d = (data ?? {});
    const questions = Array.isArray(d.questions) ? d.questions : [];
    const routedId = d.route?.question;
    return (routedId ? questions.find((x) => x?.id === routedId) : questions[0]);
}
/**
 * Which data shape belongs to which node type. `data` has to be validated
 * against the node's own `type`, not against whichever shape happens to match
 * first: as a plain z.union the trigger shape wins every time (all its fields
 * are optional), so action nodes were never shape-checked, never got the
 * `actionType` default, and every node came back with a spurious
 * `subtype: "manual"` bolted on.
 *
 * `type` is a sibling of `data` rather than a field inside it, so
 * z.discriminatedUnion cannot express this — hence the explicit lookup.
 */
const NODE_DATA_SCHEMAS = {
    trigger: triggerDataSchema,
    agent: agentDataSchema,
    condition: workflowConditionDataSchema,
    action: actionDataSchema,
    decision: workflowDecisionDataSchema,
};
/** A single node on the canvas. Keys match ReactFlow's Node shape. */
export const workflowNodeSchema = z
    .object({
    id: z.string().min(1).max(200),
    type: z.enum(WORKFLOW_NODE_TYPES),
    position: z.object({ x: z.number(), y: z.number() }),
    data: z.record(z.string(), z.unknown()),
})
    .superRefine((node, ctx) => {
    const result = NODE_DATA_SCHEMAS[node.type].safeParse(node.data);
    if (result.success)
        return;
    for (const issue of result.error.issues) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["data", ...issue.path],
            message: issue.message,
        });
    }
})
    // Re-parse so per-type defaults (trigger subtype, action actionType) land on
    // the output. Only reached once the refine above passed.
    .transform((node) => ({
    ...node,
    data: NODE_DATA_SCHEMAS[node.type].parse(node.data),
}));
/** A single edge on the canvas. Keys match ReactFlow's Edge shape. */
export const workflowEdgeSchema = z.object({
    id: z.string().min(1).max(200),
    source: z.string().min(1).max(200),
    target: z.string().min(1).max(200),
    // Binary conditions use "yes"/"no". Multiple-choice conditions use the
    // stable choice ID stored in node.data.choices.
    sourceHandle: z.string().min(1).max(100).nullable().optional(),
    targetHandle: z.string().nullable().optional(),
    label: z.string().max(200).optional(),
    animated: z.boolean().optional(),
});
/**
 * Combined character ceiling for the procedures resolved onto a single agent
 * step (node.data.skillIds). Applies to the SUM, not to each skill.
 *
 * Deliberately enforced as an error rather than a truncation. Step outputs are
 * truncated on the way to the agent (capForAgent) because a clipped result is
 * still usable; half a procedure is not — it reads as a complete instruction
 * that happens to stop, which is how an agent ends up confidently doing three
 * of five steps. The canvas validates against this too so it fails on save
 * rather than mid-run.
 */
export const WORKFLOW_STEP_PROCEDURE_CHAR_CAP = 32_000;
/**
 * How many custom run columns a workflow may define. The runs table shares
 * horizontal space with seven fixed columns, so this is a layout ceiling as
 * much as a storage one.
 */
export const WORKFLOW_RUN_COLUMN_LIMIT = 5;
/**
 * A user-defined column on the workflow runs table, populated from the run's
 * own data. Lets an operator surface (and then search/sort on) a business
 * identifier — a case number, a ticket ref — that would otherwise be buried
 * in the run's trigger payload.
 *
 * `source` is a dot-path into the run context, the SAME grammar condition
 * nodes already use for `var` (see workflow-jsonlogic resolvePath):
 *
 *   inputs.caseNumber                 — from the trigger payload
 *   steps.<nodeId>.outputs.ticketId   — from a completed step's outputs
 *
 * Restricted to those two roots so a column can't be pointed at arbitrary
 * context internals.
 */
export const workflowRunColumnSchema = z.object({
    // Stable identifier: the jsonb key values are stored under, and the
    // sort/search parameter the runs list sends. Deliberately separate from
    // `label` so renaming a column's display text doesn't orphan the values
    // already extracted under the old name.
    key: z
        .string()
        .min(1)
        .max(60)
        .regex(/^[a-zA-Z0-9_-]+$/, "Key may only contain letters, numbers, hyphens and underscores"),
    label: z.string().min(1).max(60),
    source: z
        .string()
        .min(1)
        .max(200)
        .refine((v) => v.startsWith("inputs.") || v.startsWith("steps."), "Source must start with 'inputs.' or 'steps.'"),
});
/** Column list with the per-workflow cap and a uniqueness check on `key`. */
export const workflowRunColumnsSchema = z
    .array(workflowRunColumnSchema)
    .max(WORKFLOW_RUN_COLUMN_LIMIT, `At most ${WORKFLOW_RUN_COLUMN_LIMIT} custom columns per workflow`)
    .refine((cols) => new Set(cols.map((c) => c.key)).size === cols.length, "Column keys must be unique");
export const createAgentWorkflowSchema = z.object({
    name: z.string().min(1).max(200),
    description: z.string().max(2000).optional(),
    // When a workflow is created from an agent's workspace, the new workflow is
    // seeded with an Agent node bound to this agent so it belongs to that agent
    // straight away (the "This agent" list keys off an agent node, not a column).
    agentId: z.string().uuid().optional(),
});
export const updateAgentWorkflowSchema = z
    .object({
    name: z.string().min(1).max(200).optional(),
    description: z.string().max(2000).nullable().optional(),
    status: z.enum(WORKFLOW_STATUSES).optional(),
    nodes: z.array(workflowNodeSchema).optional(),
    edges: z.array(workflowEdgeSchema).optional(),
    runColumns: workflowRunColumnsSchema.optional(),
    // The `updatedAt` the client last read. Sent so a save that would
    // overwrite someone else's newer edit is refused with a 409 instead of
    // silently winning. Same shape as updateWorkflowNodeSchema.
    expectedUpdatedAt: z.string().datetime({ offset: true }).optional(),
})
    // expectedUpdatedAt on its own is not a change, so it does not satisfy this.
    .refine((v) => Object.keys(v).some((k) => k !== "expectedUpdatedAt"), { message: "At least one field is required" });
/**
 * Author a complete workflow in one shot — used by the chat-driven
 * authoring MCP tool (create_workflow). Unlike createAgentWorkflowSchema
 * (name/description only), this accepts the full graph so an agent can build
 * the whole thing from a natural-language brief.
 *
 * No `status` field, deliberately. Activation arms schedules, webhooks and
 * mailbox subscriptions, so it is a human decision taken on the canvas. The
 * authoring skill already tells agents not to activate on a user's behalf;
 * leaving the field out is what makes that a control rather than advice.
 */
export const authorWorkflowSchema = z.object({
    name: z.string().min(1).max(200),
    description: z.string().max(2000).optional(),
    nodes: z.array(workflowNodeSchema).optional(),
    edges: z.array(workflowEdgeSchema).optional(),
});
/** Identify one saved workflow for focused authoring operations. */
export const workflowLocatorSchema = z.object({
    workflowId: z.string().uuid(),
});
/** Identify one saved node without fetching or replacing the full graph. */
export const workflowNodeLocatorSchema = workflowLocatorSchema.extend({
    nodeId: z.string().min(1).max(200),
});
/**
 * Patch only the editable parts of one node. The node id and type are
 * intentionally immutable here because changing either can invalidate every
 * connected edge and downstream `steps.<nodeId>` reference.
 *
 * `data` is deep-merged by the authoring service, so changing one field (for
 * example promptTemplate) preserves the node's other configuration.
 */
export const updateWorkflowNodeSchema = workflowNodeLocatorSchema.extend({
    patch: z
        .object({
        position: z
            .object({
            x: z.number().optional(),
            y: z.number().optional(),
        })
            .refine((value) => Object.keys(value).length > 0, {
            message: "Position patch must include x or y",
        })
            .optional(),
        data: z.record(z.string(), z.unknown()).optional(),
    })
        .refine((value) => Object.keys(value).length > 0, {
        message: "Patch must include position or data",
    }),
    expectedUpdatedAt: z.string().datetime({ offset: true }).optional(),
});
/**
 * Agent-authored workflow procedures (create_workflow_skill /
 * update_workflow_skill on the proclaw-workflows MCP server).
 *
 * The content ceiling is the same as the per-step budget: a procedure that
 * couldn't fit on a step on its own could never be attached to anything.
 */
export const authorWorkflowSkillSchema = z.object({
    name: z.string().min(1).max(100),
    contentMd: z.string().min(1).max(WORKFLOW_STEP_PROCEDURE_CHAR_CAP),
    note: z.string().max(500).optional(),
});
export const updateWorkflowSkillSchema = z.object({
    tenantSkillId: z.string().uuid(),
    contentMd: z.string().min(1).max(WORKFLOW_STEP_PROCEDURE_CHAR_CAP).optional(),
    enabled: z.boolean().optional(),
    note: z.string().max(500).optional(),
});
//# sourceMappingURL=agent-workflow.js.map